// src/admin/pages/AdminActivityLogs.jsx
import { useState, useEffect, useCallback } from "react";
import AdminLayout from "../layout/AdminLayout";
import { API_BASE_URL } from "../../config/api";
import { adminAuthHeader } from "../../utils/authHeader";
import Pagination from "../../components/Pagination";

const CATEGORIES = [
  { key: "all",          label: "All",          icon: "📋", color: "#4318FF" },
  { key: "auth",         label: "Auth",         icon: "🔐", color: "#0891b2" },
  { key: "search",       label: "Searches",     icon: "🔍", color: "#7c3aed" },
  { key: "report",       label: "Reports",      icon: "🚨", color: "#dc2626" },
  { key: "export",       label: "Exports",      icon: "📥", color: "#16a34a" },
  { key: "profile",      label: "Profile",      icon: "👤", color: "#d97706" },
  { key: "subscription", label: "Subscription", icon: "💳", color: "#0891b2" },
  { key: "admin",        label: "Admin",        icon: "⚙️",  color: "#6b7280" },
];

const STATUS_STYLE = {
  success: { bg: "#dcfce7", color: "#16a34a" },
  failed:  { bg: "#fee2e2", color: "#dc2626" },
  warning: { bg: "#fef3c7", color: "#d97706" },
};

const AdminActivityLogs = () => {
  const [logs,       setLogs]       = useState([]);
  const [stats,      setStats]      = useState({});
  const [loading,    setLoading]    = useState(true);
  const [page,       setPage]       = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total,      setTotal]      = useState(0);
  const LIMIT = 25;

  const [category, setCategory] = useState("all");
  const [search,   setSearch]   = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo,   setDateTo]   = useState("");

  // Cleanup modal
  const [cleanupModal,  setCleanupModal]  = useState(false);
  const [cleanupDays,   setCleanupDays]   = useState(90);
  const [cleaningUp,    setCleaningUp]    = useState(false);

  const fetchLogs = useCallback(async (currentPage = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage, limit: LIMIT,
        ...(category !== "all" && { category }),
        ...(search   && { search }),
        ...(dateFrom && { dateFrom }),
        ...(dateTo   && { dateTo }),
      });
      const res  = await fetch(`${API_BASE_URL}/api/logs/admin/all?${params}`, { headers: adminAuthHeader() });
      const data = await res.json();
      setLogs(data.logs || []);
      setStats(data.stats || {});
      setTotalPages(data.totalPages || 1);
      setTotal(data.total || 0);
      setPage(currentPage);
    } catch { }
    setLoading(false);
  }, [category, search, dateFrom, dateTo]);

  useEffect(() => { setPage(1); fetchLogs(1); }, [fetchLogs]);

  const handleCleanup = async () => {
    setCleaningUp(true);
    try {
      const res  = await fetch(`${API_BASE_URL}/api/logs/admin/cleanup`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json", ...adminAuthHeader() },
        body: JSON.stringify({ days: cleanupDays }),
      });
      const data = await res.json();
      alert("✅ " + data.message);
      setCleanupModal(false);
      fetchLogs(1);
    } catch { alert("Cleanup failed"); }
    setCleaningUp(false);
  };

  const fmt = (date) => new Date(date).toLocaleString("en-IN", {
    day: "numeric", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });

  return (
    <AdminLayout>
      <div className="admin-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <div>
          <p className="admin-welcome">System Monitoring</p>
          <h2 className="admin-title">Activity Logs</h2>
        </div>
        <button onClick={() => setCleanupModal(true)}
          style={{ padding: "10px 18px", background: "#fee2e2", color: "#dc2626", border: "1px solid #fecaca", borderRadius: 10, fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
          🗑️ Cleanup Old Logs
        </button>
      </div>

      {/* Stats overview */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px,1fr))", gap: 10, marginBottom: 20 }}>
        <div style={{ background: "#ede9fe", borderRadius: 12, padding: "12px 16px", textAlign: "center" }}>
          <div style={{ fontSize: 22, fontWeight: 900, color: "#4318FF" }}>{stats.totalLogs || 0}</div>
          <div style={{ fontSize: 11, color: "#4318FF", fontWeight: 600 }}>Total Logs</div>
        </div>
        <div style={{ background: "#dcfce7", borderRadius: 12, padding: "12px 16px", textAlign: "center" }}>
          <div style={{ fontSize: 22, fontWeight: 900, color: "#16a34a" }}>{stats.todayCount || 0}</div>
          <div style={{ fontSize: 11, color: "#16a34a", fontWeight: 600 }}>Today</div>
        </div>
        {CATEGORIES.filter(c => c.key !== "all").map(c => (
          <div key={c.key} onClick={() => setCategory(c.key)}
            style={{ background: category === c.key ? c.color : "#fff", borderRadius: 12, padding: "12px 16px",
              textAlign: "center", cursor: "pointer", border: `1.5px solid ${category === c.key ? c.color : "#e0e5f2"}` }}>
            <div style={{ fontSize: 16 }}>{c.icon}</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: category === c.key ? "#fff" : c.color }}>
              {stats.categories?.[c.key] || 0}
            </div>
            <div style={{ fontSize: 10, color: category === c.key ? "rgba(255,255,255,0.85)" : "#6b7280", fontWeight: 600 }}>
              {c.label}
            </div>
          </div>
        ))}
      </div>

      {/* Top active owners */}
      {stats.topOwners?.length > 0 && (
        <div style={{ background: "#fff", borderRadius: 14, padding: "16px 20px", marginBottom: 16, boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#1b2559", marginBottom: 10 }}>🏆 Most Active Owners</div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {stats.topOwners.map((o, i) => (
              <div key={i} style={{ background: "#f4f7fe", borderRadius: 10, padding: "8px 14px", fontSize: 12 }}>
                <span style={{ fontWeight: 700, color: "#1b2559" }}>{o.name}</span>
                <span style={{ color: "#6b7280", marginLeft: 6 }}>{o.count} actions</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={{ background: "#fff", borderRadius: 14, padding: "14px 18px", marginBottom: 16, boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <input placeholder="🔍 Search by owner name or email…" value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: 200, height: 36, padding: "0 12px", borderRadius: 8, border: "1px solid #e0e5f2", fontSize: 12, outline: "none", background: "#f9faff" }} />
          <select value={category} onChange={e => setCategory(e.target.value)} style={sel}>
            {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.icon} {c.label}</option>)}
          </select>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <label style={{ fontSize: 11, color: "#a3aed0" }}>From:</label>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={sel} />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <label style={{ fontSize: 11, color: "#a3aed0" }}>To:</label>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={sel} />
          </div>
          {(category !== "all" || search || dateFrom || dateTo) && (
            <button onClick={() => { setCategory("all"); setSearch(""); setDateFrom(""); setDateTo(""); }}
              style={{ padding: "7px 12px", background: "#fff1f1", color: "#ef4444", border: "1px solid #fecaca", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
              ✕ Clear
            </button>
          )}
        </div>
      </div>

      {/* Logs table */}
      <div style={{ background: "#fff", borderRadius: 16, boxShadow: "0 2px 8px rgba(0,0,0,0.04)", overflow: "hidden" }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: 48, color: "#a3aed0" }}>⏳ Loading logs...</div>
        ) : logs.length === 0 ? (
          <div style={{ textAlign: "center", padding: 48, color: "#a3aed0" }}>
            <div style={{ fontSize: 36, marginBottom: 8 }}>📋</div>
            <p style={{ fontWeight: 600, color: "#1b2559" }}>No logs found</p>
          </div>
        ) : (
          <>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ background: "#f4f7fe" }}>
                    {["Time", "Owner", "Action", "Description", "Category", "IP", "Status"].map(h => (
                      <th key={h} style={{ padding: "10px 14px", textAlign: "left", color: "#6b7280", fontWeight: 700, fontSize: 11, whiteSpace: "nowrap", borderBottom: "1px solid #e0e5f2" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log, i) => {
                    const ss  = STATUS_STYLE[log.status] || STATUS_STYLE.success;
                    const cat = CATEGORIES.find(c => c.key === log.category);
                    return (
                      <tr key={log._id} style={{ borderBottom: "1px solid #f4f7fe" }}>
                        <td style={{ padding: "10px 14px", whiteSpace: "nowrap", color: "#6b7280" }}>
                          {fmt(log.createdAt)}
                        </td>
                        <td style={{ padding: "10px 14px" }}>
                          <div style={{ fontWeight: 700, color: "#1b2559" }}>{log.user?.name || "—"}</div>
                          <div style={{ color: "#a3aed0", fontSize: 11 }}>{log.user?.email || ""}</div>
                        </td>
                        <td style={{ padding: "10px 14px", fontWeight: 700, color: "#1b2559", whiteSpace: "nowrap" }}>
                          {log.action.replace(/_/g, " ")}
                        </td>
                        <td style={{ padding: "10px 14px", color: "#6b7280", maxWidth: 220 }}>
                          <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {log.description}
                          </div>
                        </td>
                        <td style={{ padding: "10px 14px" }}>
                          <span style={{ background: `${cat?.color}15`, color: cat?.color, padding: "2px 8px", borderRadius: 20, fontWeight: 600, fontSize: 11 }}>
                            {cat?.icon} {cat?.label}
                          </span>
                        </td>
                        <td style={{ padding: "10px 14px", color: "#a3aed0", fontFamily: "monospace" }}>
                          {log.ip || "—"}
                        </td>
                        <td style={{ padding: "10px 14px" }}>
                          <span style={{ background: ss.bg, color: ss.color, padding: "2px 8px", borderRadius: 20, fontWeight: 700, fontSize: 11 }}>
                            {log.status}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ padding: "12px 20px", borderTop: "1px solid #f4f7fe" }}>
              <Pagination currentPage={page} totalPages={totalPages} total={total} limit={LIMIT}
                onPageChange={p => { setPage(p); fetchLogs(p); }} />
            </div>
          </>
        )}
      </div>

      {/* Cleanup Modal */}
      {cleanupModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "#fff", borderRadius: 16, padding: 28, width: 380, maxWidth: "95vw" }}>
            <h3 style={{ margin: "0 0 12px", color: "#dc2626" }}>🗑️ Cleanup Old Logs</h3>
            <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 16 }}>
              Delete all activity logs older than the selected number of days. This cannot be undone.
            </p>
            <label style={{ fontSize: 12, fontWeight: 700, color: "#1b2559", display: "block", marginBottom: 6 }}>Delete logs older than:</label>
            <select value={cleanupDays} onChange={e => setCleanupDays(parseInt(e.target.value))}
              style={{ width: "100%", padding: "9px 12px", borderRadius: 8, border: "1.5px solid #e0e5f2", fontSize: 13, marginBottom: 20, outline: "none" }}>
              <option value={30}>30 days</option>
              <option value={60}>60 days</option>
              <option value={90}>90 days</option>
              <option value={180}>180 days</option>
              <option value={365}>1 year</option>
            </select>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setCleanupModal(false)}
                style={{ padding: "10px 20px", borderRadius: 8, border: "1.5px solid #e0e5f2", background: "#f4f7fe", cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
                Cancel
              </button>
              <button onClick={handleCleanup} disabled={cleaningUp}
                style={{ padding: "10px 22px", borderRadius: 8, border: "none", cursor: "pointer", fontWeight: 700, fontSize: 13, color: "#fff", background: "#dc2626" }}>
                {cleaningUp ? "Deleting..." : "Delete Logs"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

const sel = { height: 36, padding: "0 12px", borderRadius: 8, border: "1px solid #e0e5f2", fontSize: 12, background: "#f9faff", color: "#1b2559", cursor: "pointer", outline: "none" };

export default AdminActivityLogs;
